import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { canEditCustoms, canViewCustoms, type CustomsBrokerDto } from '@crm/shared'
import { updateCustomsBroker } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { useAuthStore } from '../../auth/authStore'
import { PageLoader } from '../../../shared/ui/Spinner'
import { EmptyState } from '../../../shared/ui/EmptyState'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { Badge } from '../../../shared/ui/Badge'
import { confirm } from '../../../shared/ui/confirmStore'
import { toast } from '../../../shared/ui/toastStore'
import { BrokerGrid } from '../broker/BrokerGrid'
import { formatDateRu } from '../broker/brokerColDefs'
import { BrokerCardModal } from './BrokerCardModal'
import { CUSTOMS_BROKERS_KEY, useCustomsBrokers } from './useCustomsBrokers'

type BrokerRow = {
  id: string
  name: string
  contactPerson: string
  phone: string
  email: string
  note: string
  requestsCount: number
  lastRequestLabel: string
  lastRequestAt: string | null
  hiddenLabel: string
  raw: CustomsBrokerDto
}

function toRow(b: CustomsBrokerDto): BrokerRow {
  return {
    id: b.id,
    name: b.name,
    contactPerson: b.contactPerson ?? '',
    phone: b.phone ?? '',
    email: b.email ?? '',
    note: b.note ?? '',
    requestsCount: b.requestsCount,
    lastRequestLabel: formatDateRu(b.lastRequestAt),
    lastRequestAt: b.lastRequestAt,
    hiddenLabel: b.hidden ? 'Скрыт' : 'В списке',
    raw: b,
  }
}

const dash = <span className="text-gray-400">—</span>
const text = (v: string) => (v ? <span title={v}>{v}</span> : dash)

/**
 * Справочник таможенных брокеров (нетарифка, 27.09.2026): кому логист отправляет
 * файлы. Ведёт логист — добавляет, правит, скрывает; ADMIN, ГД и РОЗ смотрят.
 * Карточку можно завести и прямо из окна «Отправить брокеру».
 */
export function BrokersPage() {
  const role = useAuthStore((s) => s.user?.role)
  if (!canViewCustoms(role)) {
    return (
      <EmptyState
        title="Раздел недоступен"
        description="Брокеров видят логист, администратор, генеральный директор и РОЗ."
      />
    )
  }
  return <BrokersView canEdit={canEditCustoms(role)} />
}

function BrokersView({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const [showHidden, setShowHidden] = useState(false)
  const [card, setCard] = useState<{ broker?: CustomsBrokerDto } | null>(null)
  const query = useCustomsBrokers(true)

  const rows = useMemo(
    () => (query.data ?? []).filter((b) => showHidden || !b.hidden).map(toRow),
    [query.data, showHidden],
  )
  const hiddenCount = (query.data ?? []).filter((b) => b.hidden).length

  const toggleHidden = async (b: CustomsBrokerDto) => {
    if (!b.hidden) {
      const ok = await confirm({
        title: `Скрыть брокера «${b.name}»?`,
        message:
          'Он пропадёт из выбора при отправке брокеру. Запросы, уже отправленные ему, останутся с ним. Вернуть можно в любой момент.',
        confirmText: 'Скрыть',
      })
      if (!ok) return
    }
    try {
      await updateCustomsBroker(b.id, { hidden: !b.hidden })
      void qc.invalidateQueries({ queryKey: [...CUSTOMS_BROKERS_KEY] })
      toast.success(b.hidden ? `«${b.name}» снова в списке` : `«${b.name}» скрыт`)
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось изменить брокера'))
    }
  }

  const colDefs = useMemo<ColDef<BrokerRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Брокер',
        minWidth: 200,
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) =>
          data ? (
            <button
              type="button"
              onClick={() => setCard({ broker: data.raw })}
              className="font-medium text-brand-600 hover:text-brand-700 text-left"
            >
              {data.name}
            </button>
          ) : null,
      },
      {
        field: 'contactPerson',
        headerName: 'Контактное лицо',
        minWidth: 160,
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) => text(data?.contactPerson ?? ''),
      },
      {
        field: 'phone',
        headerName: 'Телефон',
        minWidth: 140,
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) => text(data?.phone ?? ''),
      },
      {
        field: 'email',
        headerName: 'E-mail',
        minWidth: 180,
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) => text(data?.email ?? ''),
      },
      {
        field: 'requestsCount',
        headerName: 'Запросов',
        minWidth: 100,
        filter: false,
        cellClass: 'text-right tabular-nums',
        headerClass: 'ag-right-aligned-header',
      },
      {
        field: 'lastRequestLabel',
        headerName: 'Последний запрос',
        minWidth: 140,
        filterParams: { dateMode: true },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.lastRequestAt ?? '').localeCompare(nodeB.data?.lastRequestAt ?? ''),
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) => data?.lastRequestLabel || dash,
      },
      {
        field: 'note',
        headerName: 'Примечание',
        flex: 1,
        minWidth: 200,
        cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) => text(data?.note ?? ''),
      },
      ...(showHidden
        ? [
            {
              field: 'hiddenLabel',
              headerName: 'В выборе',
              minWidth: 110,
              cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) =>
                data?.raw.hidden ? <Badge color="gray">Скрыт</Badge> : <span>В списке</span>,
            } satisfies ColDef<BrokerRow>,
          ]
        : []),
      ...(canEdit
        ? [
            {
              colId: 'actions',
              headerName: '',
              minWidth: 110,
              width: 120,
              sortable: false,
              filter: false,
              cellRenderer: ({ data }: ICellRendererParams<BrokerRow>) =>
                data ? (
                  <button
                    type="button"
                    onClick={() => void toggleHidden(data.raw)}
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    {data.raw.hidden ? 'Вернуть' : 'Скрыть'}
                  </button>
                ) : null,
            } satisfies ColDef<BrokerRow>,
          ]
        : []),
    ],
    // toggleHidden замыкает только стабильные qc и setCard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEdit, showHidden],
  )

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        <Link to="/customs" className="hover:text-gray-900">
          Нетарифка
        </Link>
        <span className="mx-2">›</span>
        <Link to="/customs/requests" className="hover:text-gray-900">
          Запросы брокеру
        </Link>
        <span className="mx-2">›</span>
        <span className="text-gray-900">Брокеры</span>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-gray-900">Брокеры</h1>
          {query.data && <span className="text-sm text-gray-500">в списке: {rows.length}</span>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {hiddenCount > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Показать скрытых ({hiddenCount})
            </label>
          )}
          {canEdit ? (
            <button
              type="button"
              onClick={() => setCard({})}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors"
            >
              + Добавить брокера
            </button>
          ) : (
            <span className="text-xs text-gray-500">Просмотр: правка доступна логисту</span>
          )}
        </div>
      </div>

      {query.isLoading ? (
        <PageLoader label="Загружаю брокеров…" />
      ) : query.isError ? (
        <ErrorMessage error={query.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Брокеров пока нет"
          description={
            canEdit
              ? 'Добавьте брокера кнопкой выше или прямо в окне «Отправить брокеру» — тогда в журнале будет видно, кому ушёл каждый файл.'
              : 'Брокеров заводит логист.'
          }
        />
      ) : (
        <BrokerGrid<BrokerRow>
          rowData={rows}
          columnDefs={colDefs}
          storageKey="crm_customs_brokers_cols_v1"
          className="h-[calc(100vh-260px)] min-h-[320px]"
          noRowsText="Нет брокеров"
        />
      )}

      {card && (
        <BrokerCardModal broker={card.broker} readOnly={!canEdit} onClose={() => setCard(null)} />
      )}
    </div>
  )
}
